// @ts-nocheck
import { Particle } from "../../entities";
import { TAU, clamp, rand, rgba } from "../../util";
import { Game } from "../game-base";

const NS_COL = "#9ad4ff";
const NS_COLLAPSE = 0.18;

function nullSpherePhase(elapsed, total) {
  if (elapsed < NS_COLLAPSE) {
    const p = elapsed / NS_COLLAPSE;
    const ease = p * p;
    return { mode: "collapse", scale: 1 - ease * 0.94, open: 1 - ease * 0.85 };
  }
  const eruptT = elapsed - NS_COLLAPSE;
  const eruptDur = Math.max(0.001, total - NS_COLLAPSE);
  const p = clamp(eruptT / eruptDur, 0, 1);
  const openEase = 1 - Math.pow(1 - Math.min(1, p * 2.8), 3);
  return { mode: "erupt", scale: 0.06 + openEase * 1.02, open: 0.15 + openEase * 0.85 };
}

Game.prototype.drawNullSphereVacuum = function (c, x, y, r, t, pulse, consumed) {
  const coreR = Math.min(64, r * 0.22);
  c.save();
  c.translate(x, y);
  c.globalCompositeOperation = "lighter";
  c.strokeStyle = NS_COL;
  c.lineWidth = 2;
  for (let i = 1; i <= 4; i++) {
    c.globalAlpha = 0.16 * (1.2 - i / 5);
    c.beginPath(); c.arc(0, 0, r * (i / 4), 0, TAU); c.stroke();
  }
  c.globalAlpha = 1;
  c.save();
  c.rotate(t * -2.2);
  for (let k = 0; k < 4; k++) {
    c.globalAlpha = 0.55 + 0.15 * pulse;
    c.strokeStyle = NS_COL;
    c.lineWidth = 2.5;
    c.beginPath();
    c.arc(0, 0, coreR * (1.4 + k * 0.35), k * Math.PI / 2, k * Math.PI / 2 + Math.PI * 0.85);
    c.stroke();
  }
  c.restore();
  c.restore();

  c.save();
  c.translate(x, y);
  const grad = c.createRadialGradient(0, 0, 0, 0, 0, coreR);
  grad.addColorStop(0, "#020408");
  grad.addColorStop(0.55, "#0a1420");
  grad.addColorStop(1, rgba(NS_COL, 0));
  c.fillStyle = grad;
  c.beginPath(); c.arc(0, 0, coreR, 0, TAU); c.fill();
  c.globalCompositeOperation = "lighter";
  c.strokeStyle = rgba(NS_COL, 0.85 + 0.15 * pulse);
  c.lineWidth = 3.5;
  c.beginPath(); c.arc(0, 0, coreR * 0.6, 0, TAU); c.stroke();
  if (consumed > 0) {
    c.globalAlpha = 0.7 + 0.3 * pulse;
    c.fillStyle = "#ffffff";
    c.font = "bold 14px sans-serif";
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillText(String(consumed), 0, 0);
  }
  c.restore();

  if (this.state === "playing" && Math.random() < 0.45) {
    const a = Math.random() * TAU, rr = r * (0.35 + 0.55 * Math.random());
    const sp = rand(120, 220);
    this.particles.push(new Particle(
      x + Math.cos(a) * rr, y + Math.sin(a) * rr,
      -Math.cos(a) * sp, -Math.sin(a) * sp,
      rand(0.25, 0.5), NS_COL, rand(1.2, 2.6), { kind: "glow", drag: 1.2 }));
  }
};

Game.prototype.drawNullSphereErupt = function (c, er, t) {
  const r = er.r || 280;
  const total = er.total || 3;
  const life = clamp(er.life ?? 0, 0, total);
  const elapsed = total - life;
  const phase = nullSpherePhase(elapsed, total);
  const pulse = 0.5 + 0.5 * Math.sin(t * 8 + (er.pulse || 0));
  const coreR = Math.min(64, r * 0.22) * phase.scale;
  const zoneR = r * phase.scale;

  c.save();
  c.translate(er.x, er.y);
  c.globalCompositeOperation = "lighter";
  c.strokeStyle = NS_COL;
  c.lineWidth = 2;
  for (let i = 1; i <= 4; i++) {
    c.globalAlpha = phase.open * 0.22 * (1.2 - i / 5);
    c.beginPath(); c.arc(0, 0, zoneR * (i / 4), 0, TAU); c.stroke();
  }
  c.globalAlpha = 1;
  c.save();
  c.rotate(t * 3.4);
  for (let k = 0; k < 4; k++) {
    c.globalAlpha = phase.open * (0.45 + 0.25 * pulse);
    c.strokeStyle = NS_COL;
    c.lineWidth = 2.8;
    c.beginPath();
    const arcR = coreR * (1.8 + k * 0.42);
    c.arc(0, 0, arcR, k * Math.PI / 2, k * Math.PI / 2 + Math.PI * 0.72);
    c.stroke();
  }
  c.restore();
  c.save();
  c.rotate(-t * 2.6);
  c.strokeStyle = "#ffffff";
  c.lineWidth = 1.5;
  for (let k = 0; k < 3; k++) {
    c.globalAlpha = phase.open * 0.35;
    c.beginPath();
    for (let i = 0; i <= 20; i++) {
      const f = i / 20;
      const a = k * TAU / 3 + f * Math.PI * 1.8;
      const rr = coreR * (0.35 + f * 1.5);
      const px = Math.cos(a) * rr, py = Math.sin(a) * rr;
      i ? c.lineTo(px, py) : c.moveTo(px, py);
    }
    c.stroke();
  }
  c.restore();
  c.restore();

  c.save();
  c.translate(er.x, er.y);
  const grad = c.createRadialGradient(0, 0, 0, 0, 0, Math.max(8, coreR * 1.4));
  grad.addColorStop(0, rgba("#ffffff", phase.open * 0.95));
  grad.addColorStop(0.35, rgba("#7ecfff", phase.open * 0.55));
  grad.addColorStop(0.75, rgba("#0a1420", phase.open * 0.35));
  grad.addColorStop(1, rgba(NS_COL, 0));
  c.fillStyle = grad;
  c.beginPath(); c.arc(0, 0, Math.max(8, coreR * 1.4), 0, TAU); c.fill();
  c.globalCompositeOperation = "lighter";
  c.strokeStyle = rgba("#ffffff", phase.open * (0.55 + 0.35 * pulse));
  c.lineWidth = 4;
  c.beginPath(); c.arc(0, 0, coreR * 0.75, 0, TAU); c.stroke();
  c.restore();

  if (phase.mode === "erupt" && this.state === "playing") {
    const burst = Math.random() < 0.75 ? 1 : 2;
    for (let n = 0; n < burst; n++) {
      const a = Math.random() * TAU;
      const sp = rand(180, 360);
      this.particles.push(new Particle(
        er.x + Math.cos(a) * coreR * 0.4, er.y + Math.sin(a) * coreR * 0.4,
        Math.cos(a) * sp, Math.sin(a) * sp,
        rand(0.28, 0.55), n % 2 ? "#ffffff" : NS_COL, rand(1.6, 3.2), { kind: "glow", drag: 0.92 }));
    }
  }
};

Game.prototype.drawNullSphereErupts = function () {
  if (!this.nullSphereErupts?.length) return;
  const c = this.ctx;
  const t = this.nowS;
  for (const er of this.nullSphereErupts) {
    this.drawNullSphereErupt(c, er, t);
  }
};

Game.prototype.commitNullSphereErupt = function (x, y, r, total) {
  if (!this.nullSphereErupts) this.nullSphereErupts = [];
  if (this.nullSphereErupts.some(e => Math.hypot(e.x - x, e.y - y) < 8 && (e.life ?? 0) > 0.05)) return;
  this.nullSphereErupts.push({
    x, y, r: r || 280,
    life: total, total,
    consumed: 0,
    pulse: this.nowS,
  });
};
